import { getNowBasedOnBooking } from '@lib/booking';
import { getActiveTripOrFirst } from '@lib/trip';
import { DateTime } from 'luxon';

import type { PhasedBooking } from '@lib/phased-booking';
import type { ApiKitTrip, Pcl } from '@tuic/api-kit';

type TripStageType = 'sea' | 'port';

export type TripStage = Omit<Pcl.Stage, 'date'> & {
  date: string;
  arrival?: DateTime;
  departure?: DateTime;
  type: TripStageType;
};

/**
 * Parses & sorts the given stages collection by arrivalDateTime in ascending order.
 */
export const parseStages = (stages: Pcl.Stage[] | undefined): TripStage[] => {
  return [...(stages ?? [])]
    .map(parseStage)
    .filter((stage): stage is TripStage => {
      return typeof stage !== 'undefined';
    })
    .sort((a, b) => {
      return new Date(a.date).valueOf() - new Date(b.date).valueOf();
    });
};

export const parseStage = (
  stage: Pcl.Stage | undefined,
): TripStage | undefined => {
  if (typeof stage?.date === 'undefined' || stage.date === null) {
    return undefined;
  }

  const arrival = stage.arrivalDateTime
    ? DateTime.fromISO(stage.arrivalDateTime, { setZone: true })
    : undefined;

  const departure = stage.departureDateTime
    ? DateTime.fromISO(stage.departureDateTime, { setZone: true })
    : undefined;

  return {
    ...stage,
    date: stage.date,
    arrival,
    departure,
    type: typeof stage.portCode === 'string' ? 'port' : 'sea',
  };
};

export const getCurrentStage = (
  booking: PhasedBooking,
): TripStage | undefined => {
  const trip = getActiveTripOrFirst(booking);
  const now = getNowBasedOnBooking(booking);
  const parsed = parseStages(trip?.stages);

  const idx = internalGetCurrentStageIdx_doNotImport(booking, parsed, now);

  if (typeof idx === 'undefined') {
    return undefined;
  }

  return parsed[idx];
};

export const getCurrentStageByTrip = (
  trip: ApiKitTrip,
  now: DateTime,
): TripStage | undefined => {
  const parsed = parseStages(trip?.stages);

  const idx = internalGetCurrentStageIdx_doNotImport(
    { phaser: {} } as PhasedBooking,
    parsed,
    now,
  );

  if (typeof idx === 'undefined') {
    return undefined;
  }

  return parsed[idx];
};

const getStageZoneName = (
  stages: TripStage[],
  index: number,
  fallback: string,
): string => {
  for (let i = index; i >= 0; i--) {
    const stage = stages[i];
    const zoned = stage?.arrival ?? stage?.departure;
    const zoneName = zoned?.zoneName;
    if (zoneName) {
      return zoneName;
    }
  }
  return fallback;
};

const internalGetCurrentStageIdx_doNotImport = (
  { phaser }: PhasedBooking,
  stages: TripStage[],
  now: DateTime,
): number | undefined => {
  const nowZone = now.zoneName ?? 'local';

  return stages.findLastIndex((stage, index) => {
    const portZone = getStageZoneName(stages, index, nowZone);
    const stageMidnight = DateTime.fromFormat(stage.date, 'yyyy-MM-dd', {
      zone: portZone,
    }).startOf('day');

    return stageMidnight <= now.setZone(portZone).startOf('day');
  });
};

/**
 * Returns the previous stage by given type from given booking.
 */
export const getPreviousStage = (
  booking: PhasedBooking,
  type: TripStageType,
): TripStage | undefined => {
  const trip = getActiveTripOrFirst(booking);
  const now = getNowBasedOnBooking(booking);
  const parsed = parseStages(trip?.stages);

  const idx = internalGetCurrentStageIdx_doNotImport(booking, parsed, now);

  if (typeof idx === 'undefined' || idx <= 0) {
    return undefined;
  }

  const previousStages = parsed.slice(0, idx);

  return previousStages.findLast(stage => stage.type === type);
};

/**
 * Returns the upcoming stage from given stages collection or undefined if there is no upcoming stage.
 */
export const getUpcomingStage = (
  booking: PhasedBooking,
  type: TripStageType,
): TripStage | undefined => {
  const trip = getActiveTripOrFirst(booking);
  const now = getNowBasedOnBooking(booking);
  const parsed = parseStages(trip?.stages);

  const idx = internalGetCurrentStageIdx_doNotImport(booking, parsed, now);

  if (typeof idx === 'undefined' || idx >= parsed.length) {
    return undefined;
  }

  const upcomingStages = parsed.slice(idx + 1);

  return upcomingStages.find(stage => stage.type === type);
};

export const getNextArrivalOrDepature = (
  booking: PhasedBooking,
  type: 'arrival' | 'departure',
): TripStage | undefined => {
  const trip = getActiveTripOrFirst(booking);
  const now = getNowBasedOnBooking(booking);
  const parsed = parseStages(trip?.stages);

  const idx = internalGetCurrentStageIdx_doNotImport(booking, parsed, now);

  if (typeof idx === 'undefined' || idx >= parsed.length) {
    return undefined;
  }

  const upcomingStages = parsed.slice(idx);

  return upcomingStages.find(stage => {
    const departureOrArrival = stage[type];

    return (
      typeof departureOrArrival !== 'undefined' && departureOrArrival > now
    );
  });
};

export const getLastArrivalOrDepature = (
  booking: PhasedBooking,
  type: 'arrival' | 'departure',
): TripStage | undefined => {
  const trip = getActiveTripOrFirst(booking);
  const now = getNowBasedOnBooking(booking);
  const parsed = parseStages(trip?.stages);

  const idx = internalGetCurrentStageIdx_doNotImport(booking, parsed, now);

  if (typeof idx === 'undefined' || idx >= parsed.length) {
    return undefined;
  }

  const recentStages = parsed.slice(0, idx + 1);

  return recentStages.findLast(stage => {
    const departureOrArrival = stage[type];

    return (
      typeof departureOrArrival !== 'undefined' && departureOrArrival < now
    );
  });
};

/**
 * Determines if the ship is currently at the port or on the sea.
 *
 * @returns `true` if the ship is currently at the port, `false` if it is on the sea.
 */
export const getCurrentPortStatus = (booking: PhasedBooking): boolean => {
  const trip = getActiveTripOrFirst(booking);
  const now = getNowBasedOnBooking(booking);
  const normalizedNow = now.setZone('UTC');
  const parsed = parseStages(trip?.stages);

  type TravelEvent = { type: 'arrival' | 'departure'; date: DateTime };

  const events: TravelEvent[] = parsed
    .flatMap(stage => [
      stage.arrival
        ? ({ type: 'arrival', date: stage.arrival } as const)
        : undefined,
      stage.departure
        ? ({ type: 'departure', date: stage.departure } as const)
        : undefined,
    ])
    .filter(action => action !== undefined);

  const [pastEvents, futureEvents] = events
    .reduce<[TravelEvent[], TravelEvent[]]>(
      ([past, future], event) => {
        const normalizedDate = event.date?.setZone('UTC');
        if (normalizedDate && normalizedDate <= normalizedNow) {
          past.push(event);
        } else {
          future.push(event);
        }

        return [past, future];
      },
      [[], []],
    )
    .map(events => events.sort((a, b) => a.date.valueOf() - b.date.valueOf()));

  const lastEvent = pastEvents[pastEvents.length - 1] as
    | TravelEvent
    | undefined;
  const nextEvent = futureEvents[0] as TravelEvent | undefined;

  if (typeof lastEvent !== 'undefined') {
    return lastEvent.type === 'arrival';
  }

  if (typeof nextEvent !== 'undefined') {
    return nextEvent.type === 'departure';
  }

  // unable to determine if we are at port -> defaults to true
  return true;
};
